import React, { useState, useEffect, useRef } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Grid,
  TextField,
  Autocomplete,
  Button,
  Stack,
  Chip,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import Page from '../../components/Page';
import Loader from '../../components/Loader';
import { useAppDispatch } from '../../store';
import { getWishlistAnalytics, WishlistAnalyticsResponse } from '../../services/wishlist-analyzer';
import { findPropertyCategory } from '../../store/propertyCategory/api';
import { findLocationManagement } from '../../store/locationManagement/api';
import { findSelectPossessionType } from '../../store/selectPossessionType/api';
import { findSelectPropertySubCategory } from '../../store/selectPropertySubCategory/api';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip as ChartTooltip, Legend } from 'chart.js';
import { Bar } from 'react-chartjs-2';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import moment from 'moment';
import FilterListIcon from '@mui/icons-material/FilterList';
import WishlistPdfExport from '../../components/WishlistPdfExport';

// Register ChartJS components
ChartJS.register(CategoryScale, LinearScale, BarElement, Title, ChartTooltip, Legend, ChartDataLabels);

const formatDateToYYYYMMDD = (date: Date): string => {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
};

const WishlistAnalyzer = () => {
  const dispatch = useAppDispatch();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<WishlistAnalyticsResponse | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  // Chart refs for PDF export
  const chart1Ref = useRef<HTMLDivElement>(null);
  const chart2Ref = useRef<HTMLDivElement>(null);
  const chart3Ref = useRef<HTMLDivElement>(null);
  const chart4Ref = useRef<HTMLDivElement>(null);

  // Date range states
  const now = new Date();
  const firstDayOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
  const lastDayOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  const [startDate, setStartDate] = useState<Date | null>(firstDayOfMonth);
  const [endDate, setEndDate] = useState<Date | null>(lastDayOfMonth);
  const [appliedStartDate, setAppliedStartDate] = useState<Date | null>(firstDayOfMonth);
  const [appliedEndDate, setAppliedEndDate] = useState<Date | null>(lastDayOfMonth);

  // Filter states
  const [selectedPossessionType, setSelectedPossessionType] = useState<any>(null);
  const [selectedLocation, setSelectedLocation] = useState<any>(null);
  const [selectedCategory, setSelectedCategory] = useState<any>(null);
  const [selectedSubCategory, setSelectedSubCategory] = useState<any>(null);
  const [searchText, setSearchText] = useState('');

  // Options for filters
  const [possessionTypes, setPossessionTypes] = useState<any[]>([]);
  const [locations, setLocations] = useState<any[]>([]);
  const [categories, setCategories] = useState<any[]>([]);
  const [subCategories, setSubCategories] = useState<any[]>([]);

  // Fetch all zones/locations
  const fetchAllLocations = async () => {
    try {
      let allLocations: any[] = [];
      let skip = 0;
      const limit = 200;
      let hasMore = true;

      while (hasMore) {
        const response = await dispatch(
          findLocationManagement({ limit, skip })
        ).unwrap();
        
        const locationsData = response?.data || [];
        allLocations = [...allLocations, ...locationsData];
        
        // Check if there are more records
        const total = response?.total || 0;
        hasMore = allLocations.length < total;
        skip += limit;
      }

      return allLocations;
    } catch (error) {
      console.error('Error fetching all locations:', error);
      return [];
    }
  };

  // Fetch filter options
  useEffect(() => {
    const fetchFilterOptions = async () => {
      try {
        const [possessionTypesRes, allLocations, categoriesRes] = await Promise.all([
          dispatch(findSelectPossessionType({})).unwrap(),
          fetchAllLocations(),
          dispatch(findPropertyCategory({ limit: 100 })).unwrap(),
        ]);

        setPossessionTypes(possessionTypesRes?.data || []);
        setLocations(allLocations);
        setCategories(categoriesRes?.data || []);
      } catch (error) {
        console.error('Error fetching filter options:', error);
      }
    };

    fetchFilterOptions();
  }, [dispatch]);

  // Fetch subcategories when category is selected
  useEffect(() => {
    const fetchSubCategories = async () => {
      if (selectedCategory?.id) {
        try {
          const response = await dispatch(
            findSelectPropertySubCategory({ categoryId: selectedCategory.id, limit: 100 })
          ).unwrap();
          setSubCategories(response?.data || []);
        } catch (error) {
          console.error('Error fetching subcategories:', error);
          setSubCategories([]);
        }
      } else {
        setSubCategories([]);
        setSelectedSubCategory(null);
      }
    };

    fetchSubCategories();
  }, [selectedCategory, dispatch]);

  // Fetch analytics data
  const fetchData = React.useCallback(async (limitOverride?: number, skipOverride?: number) => {
    try {
      setLoading(true);
      const params: any = {
        limit: limitOverride ?? rowsPerPage,
        skip: skipOverride ?? page * rowsPerPage,
      };

      if (appliedStartDate) {
        params.startDate = formatDateToYYYYMMDD(appliedStartDate);
      }
      if (appliedEndDate) {
        params.endDate = formatDateToYYYYMMDD(appliedEndDate);
      }
      // Use current filter states (they are applied when Apply Filters is clicked)
      if (selectedPossessionType?.id) {
        params.possessionTypeId = selectedPossessionType.id;
      }
      if (selectedLocation?.id) {
        params.locationId = selectedLocation.id;
      }
      if (selectedCategory?.id) {
        params.categoryId = selectedCategory.id;
      }
      if (selectedSubCategory?.id) {
        params.subCategoryId = selectedSubCategory.id;
      }
      if (searchText.trim()) {
        params.search = searchText.trim();
      }

      const response = await dispatch(getWishlistAnalytics(params)).unwrap();
      setData(response);
      return response;
    } catch (error: any) {
      console.error('Error fetching wishlist analytics:', error);
      return null;
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, appliedStartDate, appliedEndDate, selectedPossessionType, selectedLocation, selectedCategory, selectedSubCategory, searchText, dispatch]);

  // Function to fetch all data for PDF export
  const fetchAllDataForPDF = async (): Promise<WishlistAnalyticsResponse | null> => {
    try {
      const baseParams: any = {};

      if (appliedStartDate) {
        baseParams.startDate = formatDateToYYYYMMDD(appliedStartDate);
      }
      if (appliedEndDate) {
        baseParams.endDate = formatDateToYYYYMMDD(appliedEndDate);
      }
      if (selectedPossessionType?.id) {
        baseParams.possessionTypeId = selectedPossessionType.id;
      }
      if (selectedLocation?.id) {
        baseParams.locationId = selectedLocation.id;
      }
      if (selectedCategory?.id) {
        baseParams.categoryId = selectedCategory.id;
      }
      if (selectedSubCategory?.id) {
        baseParams.subCategoryId = selectedSubCategory.id;
      }
      if (searchText.trim()) {
        baseParams.search = searchText.trim();
      }

      // First request to get total count
      const firstResponse = await dispatch(
        getWishlistAnalytics({ ...baseParams, limit: 100, skip: 0 })
      ).unwrap();

      const total = firstResponse.total;
      let allWishlists = [...firstResponse.data];

      // Fetch remaining data in chunks of 100 (max allowed)
      if (total > 100) {
        const remainingChunks = Math.ceil((total - 100) / 100);
        for (let i = 1; i <= remainingChunks; i++) {
          const skip = i * 100;
          const chunkResponse = await dispatch(
            getWishlistAnalytics({ ...baseParams, limit: 100, skip })
          ).unwrap();
          allWishlists = [...allWishlists, ...chunkResponse.data];
        }
      }

      // Return combined response with all data
      return {
        ...firstResponse,
        data: allWishlists,
        total: total,
      };
    } catch (error: any) {
      console.error('Error fetching all data for PDF:', error);
      return null;
    }
  };

  // Initial load and when filters change
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Apply filters
  const handleApplyFilters = () => {
    setAppliedStartDate(startDate);
    setAppliedEndDate(endDate);
    setPage(0);
    // fetchData will be called by useEffect when appliedStartDate/appliedEndDate change
  };

  // Reset filters
  const handleResetFilters = () => {
    setStartDate(firstDayOfMonth);
    setEndDate(lastDayOfMonth);
    setAppliedStartDate(firstDayOfMonth);
    setAppliedEndDate(lastDayOfMonth);
    setSelectedPossessionType(null);
    setSelectedLocation(null);
    setSelectedCategory(null);
    setSelectedSubCategory(null);
    setSearchText('');
    setPage(0);
  };

  // Handle page change
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  // Handle rows per page change
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Prepare chart data for possession type
  const possessionTypeChartData = data?.summary ? {
    labels: ['For Sale', 'For Rent'],
    datasets: [
      {
        label: '',
        data: [data.summary.forSale, data.summary.forRent],
        backgroundColor: ['#1976d2', '#2e7d32'],
      },
    ],
  } : null;

  // Prepare chart data for locations (top 10)
  const locationChartData = data?.summary?.byLocation?.slice(0, 10) ? {
    labels: data.summary.byLocation.slice(0, 10).map((loc: any) => loc.locationName),
    datasets: [
      {
        label: '',
        data: data.summary.byLocation.slice(0, 10).map((loc: any) => loc.count),
        backgroundColor: '#1976d2',
      },
    ],
  } : null;

  // Prepare chart data for categories (top 10)
  const categoryChartData = data?.summary?.byCategory?.slice(0, 10) ? {
    labels: data.summary.byCategory.slice(0, 10).map((cat: any) => cat.categoryName),
    datasets: [
      {
        label: '',
        data: data.summary.byCategory.slice(0, 10).map((cat: any) => cat.count),
        backgroundColor: '#2e7d32',
      },
    ],
  } : null;

  // Prepare chart data for subcategories (top 10)
  const subCategoryChartData = data?.summary?.bySubCategory?.slice(0, 10) ? {
    labels: data.summary.bySubCategory.slice(0, 10).map((subCat: any) => subCat.subCategoryName),
    datasets: [
      {
        label: '',
        data: data.summary.bySubCategory.slice(0, 10).map((subCat: any) => subCat.count),
        backgroundColor: '#ed6c02',
      },
    ],
  } : null;

  const chartOptions = {
    responsive: true,
    layout: {
      padding: {
        top: 40,
        bottom: 0,
        left: 0,
        right: 0,
      },
    },
    plugins: {
      legend: {
        display: false,
      },
      datalabels: {
        display: true,
        anchor: 'end' as const,
        align: 'top' as const,
        offset: 10,
        font: {
          weight: 'bold' as const,
          size: 12,
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          precision: 0,
        },
      },
    },
  };

  return (
    <Page title="Wishlist Analyzer">
      <Container maxWidth="xl">
        <Box sx={{ mb: 3 }}>
            <Typography variant="h4" gutterBottom>
              Wishlist Analyzer
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Analyze wishlist trends and user preferences
            </Typography>
        </Box>

        {/* Filters Card */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={3}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    label="Start Date"
                    value={startDate}
                    onChange={(newValue) => setStartDate(newValue)}
                    renderInput={(params) => <TextField {...params} fullWidth />}
                  />
                </LocalizationProvider>
              </Grid>
              <Grid item xs={12} md={3}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    label="End Date"
                    value={endDate}
                    onChange={(newValue) => setEndDate(newValue)}
                    renderInput={(params) => <TextField {...params} fullWidth />}
                  />
                </LocalizationProvider>
              </Grid>
              <Grid item xs={12} md={2}>
                <Autocomplete
                  options={possessionTypes}
                  getOptionLabel={(option) => option?.labelEn || ''}
                  value={selectedPossessionType}
                  onChange={(_, newValue) => setSelectedPossessionType(newValue)}
                  renderInput={(params) => (
                    <TextField {...params} label="Possession Type" fullWidth />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <Autocomplete
                  options={locations}
                  getOptionLabel={(option) => option?.labelEn || ''}
                  value={selectedLocation}
                  onChange={(_, newValue) => setSelectedLocation(newValue)}
                  renderInput={(params) => (
                    <TextField {...params} label="Location" fullWidth />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <Autocomplete
                  options={categories}
                  getOptionLabel={(option) => option?.labelEn || ''}
                  value={selectedCategory}
                  onChange={(_, newValue) => {
                    setSelectedCategory(newValue);
                    setSelectedSubCategory(null); // Reset subcategory when category changes
                  }}
                  renderInput={(params) => (
                    <TextField {...params} label="Category" fullWidth />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <Autocomplete
                  options={subCategories}
                  getOptionLabel={(option) => option?.labelEn || ''}
                  value={selectedSubCategory}
                  onChange={(_, newValue) => setSelectedSubCategory(newValue)}
                  disabled={!selectedCategory}
                  renderInput={(params) => (
                    <TextField {...params} label="Sub Category" fullWidth />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Search by Name"
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  placeholder="Search wishlist name..."
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Stack direction="row" spacing={2}>
                  <LoadingButton
                    variant="contained"
                    onClick={handleApplyFilters}
                    loading={loading}
                    startIcon={<FilterListIcon />}
                  >
                    Apply Filters
                  </LoadingButton>
                  <Button variant="outlined" onClick={handleResetFilters}>
                    Reset
                  </Button>
                  <WishlistPdfExport
                    data={data}
                    startDate={appliedStartDate}
                    endDate={appliedEndDate}
                    chart1Ref={chart1Ref}
                    chart2Ref={chart2Ref}
                    chart3Ref={chart3Ref}
                    chart4Ref={chart4Ref}
                    loading={loading}
                    formatDateToYYYYMMDD={formatDateToYYYYMMDD}
                    fetchAllData={fetchAllDataForPDF}
                    selectedPossessionType={selectedPossessionType}
                    selectedLocation={selectedLocation}
                    selectedCategory={selectedCategory}
                    selectedSubCategory={selectedSubCategory}
                    searchText={searchText}
                  />
                </Stack>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Summary Cards */}
        {data?.summary && (
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={3}>
              <Card>
                <CardContent>
                  <Typography color="text.secondary" gutterBottom>
                    Total Wishlists
                  </Typography>
                  <Typography variant="h4">{data.summary.total}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={3}>
              <Card>
                <CardContent>
                  <Typography color="text.secondary" gutterBottom>
                    For Sale
                  </Typography>
                  <Typography variant="h4">{data.summary.forSale}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={3}>
              <Card>
                <CardContent>
                  <Typography color="text.secondary" gutterBottom>
                    For Rent
                  </Typography>
                  <Typography variant="h4">{data.summary.forRent}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={3}>
              <Card>
                <CardContent>
                  <Typography color="text.secondary" gutterBottom>
                    Current Page
                  </Typography>
                  <Typography variant="h4">{data.data?.length || 0}</Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

        {/* Charts */}
        {data && (
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    By Possession Type
                  </Typography>
                  <div ref={chart1Ref}>
                    {possessionTypeChartData && (
                      <Bar data={possessionTypeChartData} options={chartOptions} />
                    )}
                  </div>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Top Locations
                  </Typography>
                  <div ref={chart2Ref}>
                    {locationChartData && (
                      <Bar data={locationChartData} options={chartOptions} />
                    )}
                  </div>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Top Categories
                  </Typography>
                  <div ref={chart3Ref}>
                    {categoryChartData && (
                      <Bar data={categoryChartData} options={chartOptions} />
                    )}
                  </div>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Top Sub Categories
                  </Typography>
                  <div ref={chart4Ref}>
                    {subCategoryChartData && (
                      <Bar data={subCategoryChartData} options={chartOptions} />
                    )}
                  </div>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

        {/* Data Table */}
        <Card>
          <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
            <Box sx={{ p: 3 }}>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <Loader />
              </Box>
            ) : (
              <>
                <TableContainer sx={{ width: '100%', overflowX: 'auto', position: 'relative' }}>
                  <Table sx={{ minWidth: 1000, width: '100%' }}>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ minWidth: 150 }}>Wishlist Name</TableCell>
                          <TableCell sx={{ minWidth: 120 }}>User</TableCell>
                          <TableCell sx={{ minWidth: 120 }}>Possession Type</TableCell>
                          <TableCell sx={{ minWidth: 180 }}>Location</TableCell>
                          <TableCell sx={{ minWidth: 100 }}>Category</TableCell>
                          <TableCell sx={{ minWidth: 130 }}>Sub Category</TableCell>
                          <TableCell sx={{ minWidth: 130 }}>Price Range</TableCell>
                          <TableCell sx={{ minWidth: 140 }}>Created Date</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {data?.data && data.data.length > 0 ? (
                          data.data.map((wishlist) => (
                            <TableRow key={wishlist.id} sx={{ height: 'auto' }}>
                            <TableCell sx={{ py: 1.5, wordBreak: 'break-word', verticalAlign: 'top' }}>
                              <Box>
                                <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>{wishlist.name}</Typography>
                                <Typography variant="caption" color="primary" sx={{ fontWeight: 'bold' }}>
                                  {wishlist.matchingAdsCount || 0} active ads
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell sx={{ py: 1.5, wordBreak: 'break-word', verticalAlign: 'top' }}>
                              <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                                {wishlist.userName}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ py: 1.5, verticalAlign: 'top' }}>
                              <Chip
                                label={wishlist.possessionType}
                                color={wishlist.possessionType === 'For Sale' ? 'primary' : 'success'}
                                size="small"
                              />
                            </TableCell>
                            <TableCell sx={{ py: 1.5, wordBreak: 'break-word', verticalAlign: 'top', whiteSpace: 'normal' }}>
                              {wishlist.locations && wishlist.locations.length > 0 ? (
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, width: '100%' }}>
                                  {wishlist.locations.map((loc: any, idx: number) => (
                                    <Box key={idx} sx={{ display: 'block', width: '100%', lineHeight: 1.5 }}>
                                      <Chip
                                        label={loc.locationName}
                                        size="small"
                                        variant="outlined"
                                        sx={{ 
                                          display: 'inline-flex',
                                          width: 'auto'
                                        }}
                                      />
                                    </Box>
                                  ))}
                                </Box>
                              ) : (
                                <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                                  {wishlist.location || 'Unknown'}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell sx={{ py: 1.5, wordBreak: 'break-word', verticalAlign: 'top' }}>
                              <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                                {wishlist.category}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ py: 1.5, wordBreak: 'break-word', verticalAlign: 'top' }}>
                              <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                                {wishlist.subCategory || 'N/A'}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ py: 1.5, wordBreak: 'break-word', verticalAlign: 'top' }}>
                              <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                                {wishlist.minPrice.toLocaleString()} - {wishlist.maxPrice.toLocaleString()}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ py: 1.5, verticalAlign: 'top' }}>
                              <Typography variant="body2">
                                {moment(wishlist.createdAt).format('YYYY-MM-DD HH:mm')}
                              </Typography>
                            </TableCell>
                            </TableRow>
                          ))
                        ) : (
                          <TableRow>
                            <TableCell colSpan={8} align="center">
                              No wishlists found
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                  component="div"
                  count={data?.total || 0}
                  page={page}
                  onPageChange={handleChangePage}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  rowsPerPageOptions={[10, 25, 50, 100]}
                />
              </>
            )}
            </Box>
          </CardContent>
        </Card>
      </Container>
    </Page>
  );
};

export default WishlistAnalyzer;

